import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DualChartsCardComponent } from '../../../shared/dual-charts-card/dual-charts-card.component';
import { CATEGORIES_OPTIONS, IMPACT_SPEND_OPTIONS, SCOPES_OPTIONS } from '../../utils/chart-options';
import { FormGroup } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, finalize, startWith, switchMap, tap } from 'rxjs/operators';
import { Scope3Service } from '../../services/scope-3.service';
import { Scope3Hotspots, Scope3ScopesAndCategories } from '@mds/common';
import { of } from 'rxjs';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { ButtonToggleComponent } from '@mds/ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-hotspot-charts',
  standalone: true,
  imports: [CommonModule, DualChartsCardComponent, MdsIconsModule, ButtonToggleComponent],
  templateUrl: './hotspot-charts.component.html',
  styleUrls: ['./hotspot-charts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HotspotChartsComponent implements OnInit {
  impactChartOptions: Highcharts.Options;
  spendChartOptions: Highcharts.Options;
  scopesChartOptions: Highcharts.Options;
  categoriesChartOptions: Highcharts.Options;
  loading1 = true;
  loading2 = true;
  hasHotspots: boolean;
  hasScopes: boolean;
  private scope3Service = inject(Scope3Service);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  _filters: FormGroup;

  get filters() {
    return this._filters;
  }

  @Input() set filters(values: FormGroup) {
    this._filters = values;
    this.cdr.markForCheck();
  }

  toggleData = [
    {
      value: 'Hostspots',
      icon: MdsIcon.BREAKDOWN,
      label: 'Hostspots',
      ariaLabel: 'Hostspots Icon'
    },
    {
      value: 'Total emissions',
      icon: MdsIcon.HOTSPOTS,
      label: 'Total emissions',
      ariaLabel: 'Emissions'
    }
  ];
  isToggleChart = true;
  get commonFilters() {
    const filters = this.filters.getRawValue();
    const commonFilters: any = {
      aggregate_by: filters.aggregation,
      category_scope3: filters.category.map((c) => c.value) || [],
      product_group_rids: filters.productGroup.map((c) => c.value) || [],
      quality_name: filters.dataQuality.map((c) => c.value) || [],
      receiver_site_rids: filters.buyerSite.map((c) => c.value) || [],
      supplier_site_rids: filters.supplierSite.map((c) => c.value) || [],
      spend_class: filters.spendClass.map((c) => c.value) || [],
      year: filters.year[0].value
    };
    if (filters.period[0].value) {
      commonFilters.quarter_range = filters.period[0].value;
    }
    return commonFilters;
  }

  ngOnInit(): void {
    this.filters.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(this.filters.value),
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.loading1 = true;
        }),
        switchMap(() =>
          this.scope3Service.getHotspots(this.commonFilters).pipe(
            catchError(() => of(null)) // Allow execution to continue
          )
        )
      )
      .subscribe((hotspots: Scope3Hotspots) => {
        if (hotspots) {
          this.hasHotspots = !!hotspots.impact_hotspots.length && !!hotspots.spend_hotspots.length;
          if (this.hasHotspots) {
            const impactData = hotspots.impact_hotspots
              .filter((r, i) => i < 5)
              .map((d) => ({
                name: d.name,
                y: d.value
              }));
            const spendData = hotspots.spend_hotspots
              .filter((r, i) => i < 5)
              .map((d) => ({
                name: d.name,
                y: d.value
              }));
            this.updateImpactChart(impactData, 'Impact', hotspots.total_impact, 'CO2/kg');
            this.updateSpendChart(spendData, 'Spend', hotspots.total_spend, hotspots.currency ?? 'USD');
          }
        }
        this.loading1 = false;
        this.getScopesAndCategories();
        this.cdr.markForCheck();
      });
  }

  onSwitchView() {
    this.isToggleChart = !this.isToggleChart;
  }

  private getScopesAndCategories() {
    this.loading2 = true;
    this.scope3Service
      .getScopesAndCategories(this.commonFilters)
      .pipe(
        finalize(() => {
          this.loading2 = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((scopesAndCategories: Scope3ScopesAndCategories) => {
        this.hasScopes = !!scopesAndCategories?.scopes_percentage?.scope_3.length;
        if (this.hasScopes) {
          const scopesTotal = Object.values(scopesAndCategories?.scopes_total) ?? [];
          const categoriesData =
            scopesAndCategories?.scopes_percentage?.scope_3.map((category) => ({
              name: category.name,
              y: category.value
            })) ?? [];
          this.updateScopesChart(scopesTotal);
          this.updateCategoriesChart(categoriesData);
        }
        this.cdr.markForCheck();
      });
  }

  private updateCategoriesChart(series: any) {
    this.categoriesChartOptions = this.getCategoriesOptions(series);
  }

  private updateScopesChart(series: any) {
    this.scopesChartOptions = this.getScopesOptions(series);
  }

  private getScopesOptions(emissionData: any[]) {
    return {
      ...structuredClone(SCOPES_OPTIONS),
      series: [
        {
          name: 'Emissions',
          type: 'column',
          data: emissionData,
          colorByPoint: true
        }
      ]
    } as Highcharts.Options;
  }

  private getCategoriesOptions(data: any[]) {
    return {
      ...structuredClone(CATEGORIES_OPTIONS),
      series: [
        {
          name: 'Category',
          type: 'pie',
          data
        }
      ]
    } as Highcharts.Options;
  }

  private updateImpactChart(data: any, name = '', value = 0, unit = '') {
    this.impactChartOptions = this.getImpactSpendOptions(data, name, value, unit);
  }

  private updateSpendChart(data: any, name = '', value = 0, unit = '') {
    this.spendChartOptions = this.getImpactSpendOptions(data, name, value, unit);
  }

  private getImpactSpendOptions(data: number[], chartName: string, value: number, unit: string) {
    return {
      ...structuredClone(
        IMPACT_SPEND_OPTIONS(chartName, value, unit, ['#00747A', '#219491', '#01C9B3', '#80E4D9', '#BFF1EC'])
      ),
      series: [{ type: 'pie', data }]
    } as Highcharts.Options;
  }
}
